import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Box, Flex, CloseButton, Text } from '@chakra-ui/react';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };

    setToasts(prev => [...prev, toast]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
  const error   = useCallback((message, duration) => addToast(message, 'error',   duration), [addToast]);
  const info    = useCallback((message, duration) => addToast(message, 'info',    duration), [addToast]);
  const warning = useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]);

  const contextValue = useMemo(
    () => ({ addToast, removeToast, success, error, info, warning }),
    [addToast, removeToast, success, error, info, warning]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const TOAST_STYLES = {
  success: { bg: '#ECFDF5', border: '#059669', icon: CheckCircle, iconColor: '#059669', textColor: '#065F46' },
  error:   { bg: '#FEF2F2', border: '#DC2626', icon: XCircle,     iconColor: '#DC2626', textColor: '#991B1B' },
  warning: { bg: '#FFFBEB', border: '#D97706', icon: AlertTriangle,iconColor: '#D97706', textColor: '#92400E' },
  info:    { bg: '#EFF6FF', border: '#1B4FCC', icon: Info,         iconColor: '#1B4FCC', textColor: '#1E40AF' },
};

const ToastContainer = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <Box
      position="fixed"
      top={4}
      right={4}
      zIndex={9999}
      display="flex"
      flexDir="column"
      gap={2}
      maxW="380px"
      w="full"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </Box>
  );
};

const Toast = ({ toast, onRemove }) => {
  const cfg = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
  const Icon = cfg.icon;

  return (
    <Flex
      align="flex-start"
      gap={3}
      p={4}
      bg={cfg.bg}
      borderWidth="1px"
      borderColor={cfg.border}
      borderRadius="lg"
      boxShadow="0 4px 12px rgba(0,0,0,0.12)"
      role="alert"
      aria-live="assertive"
      style={{ animation: 'slideIn 200ms ease' }}
    >
      <Box pt="1px" flexShrink={0}>
        <Icon size={16} color={cfg.iconColor} strokeWidth={2.5} />
      </Box>
      <Text flex={1} fontSize="sm" fontWeight="500" color={cfg.textColor} lineHeight="1.5">
        {toast.message}
      </Text>
      <CloseButton
        size="sm"
        color={cfg.textColor}
        onClick={() => onRemove(toast.id)}
        aria-label="Close notification"
        mt="-2px"
        mr="-4px"
        opacity={0.6}
        _hover={{ opacity: 1 }}
      />
    </Flex>
  );
};

